import React from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import type { ObligationsData, Obligation, ObligationCalendarDay } from './use-finance-obligations.js';

function fmt(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return '\u2014';
  const dt = new Date(d + 'T12:00:00');
  return isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const STATUS_STYLE: Record<string, { color: string; bg: string }> = {
  OVERDUE:  { color: C.red, bg: 'rgba(255,107,107,0.12)' },
  DUE_SOON: { color: '#FBBF24', bg: 'rgba(251,191,36,0.12)' },
  UPCOMING: { color: C.textMuted, bg: 'rgba(255,255,255,0.05)' },
  PAID:     { color: C.green, bg: 'rgba(78,205,196,0.12)' },
};

function ObligationRow({ ob }: { ob: Obligation }) {
  const st = STATUS_STYLE[ob.status] || STATUS_STYLE.UPCOMING;
  return (
    <div style={{ padding: 14, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}`, display: 'grid', gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14, color: C.text, fontWeight: 600 }}>{ob.obligation_name}</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: C.gold }}>{fmt(ob.amount)}</span>
          <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999, color: st.color, background: st.bg }}>{ob.status.replace(/_/g, ' ')}</span>
        </div>
      </div>
      <div style={{ fontSize: 12, color: C.textMuted, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        {ob.vendor && <span>Vendor: {ob.vendor}</span>}
        {ob.category && <span>Category: {ob.category}</span>}
        {ob.frequency && <span>Freq: {ob.frequency}</span>}
        {ob.due_date && <span>Due: {fmtDate(ob.due_date)}</span>}
        {ob.days_until_due != null && ob.status !== 'PAID' && (
          <span style={{ color: ob.days_until_due < 0 ? C.red : ob.days_until_due <= 7 ? '#FBBF24' : C.textMuted }}>
            {ob.days_until_due < 0 ? `${Math.abs(ob.days_until_due)}d overdue` : `${ob.days_until_due}d`}
          </span>
        )}
      </div>
      {ob.last_payment && (
        <div style={{ fontSize: 11, color: C.green }}>
          Last paid: {fmt(ob.last_payment.amount_paid)} on {fmtDate(ob.last_payment.date_paid)}
          {ob.last_payment.confirmation_number ? ` \u00b7 #${ob.last_payment.confirmation_number}` : ''}
        </div>
      )}
      {ob.notes && <div style={{ fontSize: 11, color: C.textMuted }}>{ob.notes}</div>}
    </div>
  );
}

interface Props { data: ObligationsData; onRefresh: () => void; }

export default function FinanceObligations({ data, onRefresh }: Props) {
  const { obligations, summary, by_category, calendar, weekly_outlook, as_of, range_days } = data;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{fmt(summary.total_due_this_period)}</div>
          <div style={{ fontSize: 12, color: C.textMuted }}>Total due \u00b7 next {range_days} days \u00b7 as of {fmtDate(as_of)}</div>
        </div>
        <button onClick={onRefresh} style={{ background: 'none', border: `1px solid ${C.borderSoft}`, borderRadius: 6, color: C.textMuted, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>Refresh</button>
      </div>

      {/* Status summary */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {[
          { label: 'Overdue', count: summary.overdue_count, total: summary.overdue_total, color: C.red },
          { label: 'Due Soon', count: summary.due_soon_count, total: summary.due_soon_total, color: '#FBBF24' },
          { label: 'Upcoming', count: summary.upcoming_count, total: summary.upcoming_total, color: C.textMuted },
          { label: 'Paid', count: summary.paid_count, total: summary.paid_total, color: C.green },
        ].filter(s => s.count > 0).map(s => (
          <div key={s.label} style={{ padding: '10px 16px', borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}`, textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.count}</div>
            <div style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase' }}>{s.label}</div>
            <div style={{ fontSize: 12, color: s.color }}>{fmt(s.total)}</div>
          </div>
        ))}
      </div>

      {/* Weekly outlook */}
      {weekly_outlook.some(w => w.total > 0) && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>4-week outlook</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
            {weekly_outlook.map((w, i) => (
              <div key={i} style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}`, textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: C.textMuted }}>{fmtDate(w.week_start)} \u2013 {fmtDate(w.week_end)}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: w.total > 0 ? C.text : C.textMuted, marginTop: 4 }}>{w.total > 0 ? fmt(w.total) : '\u2014'}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Calendar */}
      {calendar.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>Upcoming due dates</div>
          <div style={{ display: 'grid', gap: 6 }}>
            {calendar.map((day: ObligationCalendarDay) => (
              <div key={day.date} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: 8, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
                <div>
                  <span style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{fmtDate(day.date)}</span>
                  <span style={{ fontSize: 11, color: C.textMuted, marginLeft: 8 }}>{day.items.join(', ')}</span>
                </div>
                <span style={{ fontSize: 13, color: C.gold, fontWeight: 600 }}>{fmt(day.total_due)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Obligation list */}
      {obligations.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>All obligations ({obligations.length})</div>
          <div style={{ display: 'grid', gap: 8 }}>
            {obligations.map(ob => <ObligationRow key={ob.id} ob={ob} />)}
          </div>
        </div>
      )}
    </div>
  );
}
